/**
 * @file invoiceVariationReview.js
 * @version 2.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description Variation Review — one set of rules for the screen
 *              (VariationReview, opened from Pricing & Costing's header) and
 *              the server
 *              (utils/invoiceVariationReview.js is the generated server copy;
 *              scripts/sync-shared-utils.js).
 *
 *   Cam, 3 Nov 2026 (month end): "Can the Invoice Detail report disable the
 *   variation report button unless the to and from dates are both prior to
 *   the current date please? Also can the variation report modal … allow user
 *   to approve or not approve variations via a checkbox per variation please,
 *   and then a button for when they're ready to review their decisions which
 *   goes to the next stage … The next stage would offer actions to the user as
 *   well as appropriate input fields depending on their chosen action, and
 *   then the action can be performed for them from there, with audit history
 *   logging for every approval and edit? The options would usually be to edit
 *   a sale price with a note or make the invoice a draft with a note and flag
 *   the invoice in the alerts with the note until the person that flagged it
 *   clears the flag."
 *
 *   A VARIATION is one customer (account) and one product (its item code,
 *   else the line's words — `product_key`) whose invoice lines in the period
 *   carry more than one unit price ex VAT (the Variation Summary's rule,
 *   routes/reports.js). It is decided as a whole — Approved (ticked) or Not
 *   approved — and the actions of the second stage work on its invoices.
 *
 *   The period (2.0.0): any From / To up to a year apart — this month and
 *   next month included (Cam, 3 Nov 2026: booked-over invoices are dated next
 *   month). It was: both before today.
 *
 *   Actions, per invoice of a not-approved variation:
 *     leave       nothing happens (the not-approved decision is the record)
 *     edit_price  each of its lines of the product at a new unit price (ex
 *                 VAT, per the line's unit) — through the ordinary invoice
 *                 edit; a note is required (the edit's reason)
 *     flag        the invoice flagged in the Alerts with the note, left as it
 *                 is, until the person who flagged it clears the flag
 *     draft_flag  the invoice made a DRAFT (off statements) and flagged;
 *                 clearing the flag approves it again. Only for an invoice
 *                 not in Xero — Xero cannot turn an approved invoice back
 *                 into a draft.
 *
 *   Dates are London calendar days 'YYYY-MM-DD'.
 *
 *   1.1.0 (2026-11-03, Cam — "We need to be able to make notes on invoices via
 *   the variation report modal … regardless of the chosen action, including
 *   the first screen where approving variations - why is the variation okay
 *   (for example)?"): a note per variation on its decision (optional — why it
 *   is okay, or not; saved with the decision, editable later, recorded on
 *   each of its invoices), and a note on every action, Leave it included
 *   (optional there — a Leave it with a note records the note and changes
 *   nothing else). countActions / performQuestion count a noted Leave it.
 *   A till invoice booked to account takes "Edit the sale price" too (its
 *   price is typed ex VAT here and goes on as the VAT-inclusive price Xero
 *   holds — utils/tillInvoiceEdit on the server).
 *
 *   2.0.0 (2026-11-03, Cam — "take this out of the Invoice Detail report
 *   component … have it as a standalone component with step 1 being to choose
 *   the period (this month, last month, next month, and custom date range
 *   options), and then the existing two steps combined into just one step
 *   where approval is one option (with optional reason) among the other
 *   options, saving as they go … once resolved these changes can be reversed
 *   or changed again"; "this should only be for till sales that are book to
 *   account"; "allow bulk approval of a contact's variations with one shared
 *   note"): periodBlock / periodPresets / presetFor / periodWords (no
 *   before-today rule); THE row order (sortVariations — customer by account
 *   number then name, product by code then words, invoice by date then
 *   number); one choice per variation (VARIATION_CHOICES — Approve · Leave it
 *   · the invoice actions); resolutionOf (resolved = approved, left, or every
 *   invoice acted on); customersOf / summaryOf; writeQuestion (the one quick
 *   question before an action that writes to an invoice). The `_source` is now
 *   "Variation Review".
 */

import { lockMonthToday, lockMonthUkDate, lockMonthName } from './xeroLockMonth';

/** Longest note kept on a decision, an action or a flag. */
export const NOTE_MAX = 500;

/** The `_source` every row this review writes carries. */
export const VARIATION_SOURCE = 'Variation Review';

/** The refusal's code when the period cannot be reviewed. */
export const VARIATION_PERIOD_CODE = 'VARIATION_PERIOD';

/** What the window says before migrations/invoice_variation_reviews.sql has run. */
export const REVIEW_NOT_READY = 'Not ready — migration invoice_variation_reviews.sql has not run';

/** The notice that lists open flags (config/noticeRegistry.js). */
export const FLAG_NOTICE_KEY = 'invoice_variation_flags';

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** The choices of the second stage, in the order the menu offers them. */
export const VARIATION_ACTIONS = Object.freeze([
  { value: 'leave', label: 'Leave it', icon: 'do_not_disturb_on' },
  { value: 'edit_price', label: 'Edit the sale price', icon: 'edit' },
  { value: 'flag', label: 'Flag it (leave the invoice as it is)', icon: 'flag' },
  { value: 'draft_flag', label: 'Make it a draft and flag it', icon: 'edit_note' }
]);

const ACTION_VALUES = new Set(VARIATION_ACTIONS.map(a => a.value));

/** Does this action need a note? Every one but Leave. */
export function needsNote(action) {
  return action === 'edit_price' || action === 'flag' || action === 'draft_flag';
}

/** The note box's prompt for an action (every action takes a note; Leave it's is optional). */
export function notePlaceholder(action) {
  if (action === 'edit_price') return "Why the price is changing (required — kept as the edit's reason, here and in Xero)";
  if (action === 'flag' || action === 'draft_flag') return 'Why it is flagged (required — shown in the Alerts until the flag is cleared)';
  return 'A note for the invoice, if you want one (optional — e.g. why it is left as it is; kept in its history)';
}

/** The decision note's prompt (stage 1). */
export const DECISION_NOTE_PLACEHOLDER = 'Why is this variation okay — or not? (optional; kept on each of its invoices)';

/** Does this chosen action do anything — a non-Leave, or a Leave it with a note? */
export function actionDoesSomething(a) {
  if (!a || !a.action) return false;
  if (a.action !== 'leave') return true;
  return !!cleanNote(a.note);
}

/** A variation's identity within a period: its account and its product. */
export function variationKey(accountId, productKey) {
  return `${Number(accountId)}::${String(productKey ?? '')}`;
}

/** The account and product of a key (variationKey's inverse). */
export function parseVariationKey(key) {
  const s = String(key || '');
  const at = s.indexOf('::');
  if (at < 1) return null;
  const accountId = Number(s.slice(0, at));
  const productKey = s.slice(at + 2);
  if (!Number.isInteger(accountId) || accountId <= 0 || !productKey) return null;
  return { accountId, productKey };
}

/** The longest period reviewed at once (days, From and To included). */
export const PERIOD_MAX_DAYS = 366;

const dayNumber = (iso) => {
  const [y, m, d] = String(iso).split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / 86400000);
};

/**
 * Why this period cannot be reviewed, or null when it can: both dates set,
 * From on or before To, at most PERIOD_MAX_DAYS. Any days — this month and
 * next month included (2.0.0; booked-over invoices are dated ahead).
 * @param {string} startDate 'YYYY-MM-DD'
 * @param {string} endDate   'YYYY-MM-DD'
 */
export function periodBlock(startDate, endDate) {
  const from = String(startDate || '').slice(0, 10);
  const to = String(endDate || '').slice(0, 10);
  if (!ISO_DAY.test(from) || !ISO_DAY.test(to)) return 'Choose a From and To date';
  if (from > to) return 'The From date is after the To date';
  if (dayNumber(to) - dayNumber(from) + 1 > PERIOD_MAX_DAYS) return `Choose a period of at most a year (${PERIOD_MAX_DAYS} days)`;
  return null;
}

const pad2 = (n) => String(n).padStart(2, '0');
/** A calendar month's first and last day; `m` 0-based, may run past either end of the year. */
function monthRange(y, m) {
  const first = new Date(Date.UTC(y, m, 1));
  const last = new Date(Date.UTC(y, m + 1, 0));
  const iso = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  return { from: iso(first), to: iso(last) };
}

/**
 * Step 1's choices for today (London): this month, last month, next month,
 * then Custom — each month with its dates and name ("October 2026").
 */
export function periodPresets(today = lockMonthToday()) {
  const [y, m] = String(today).split('-').map(Number);
  const named = (value, label, offset) => {
    const r = monthRange(y, m - 1 + offset);
    return { value, label, ...r, month: lockMonthName(r.from) };
  };
  return [
    named('this-month', 'This month', 0),
    named('last-month', 'Last month', -1),
    named('next-month', 'Next month', 1),
    { value: 'custom', label: 'Custom dates', from: null, to: null, month: null }
  ];
}

/** The preset these dates are (a whole this / last / next month), else 'custom'. */
export function presetFor(from, to, today = lockMonthToday()) {
  const hit = periodPresets(today).find(p => p.from && p.from === from && p.to === to);
  return hit ? hit.value : 'custom';
}

/** "October 2026" for a whole month, else "01/10/2026 – 15/10/2026". */
export function periodWords(from, to) {
  if (!ISO_DAY.test(String(from || '')) || !ISO_DAY.test(String(to || ''))) return '';
  const whole = monthRange(Number(from.slice(0, 4)), Number(from.slice(5, 7)) - 1);
  if (whole.from === from && whole.to === to) return lockMonthName(from);
  return `${lockMonthUkDate(from)} – ${lockMonthUkDate(to)}`;
}

/** A note as kept: one line, trimmed, at most NOTE_MAX characters. */
export function cleanNote(value) {
  return String(value ?? '').replace(/\s+/g, ' ').trim().slice(0, NOTE_MAX);
}

/**
 * A typed unit price: more than £0, at most 4 decimal places, or null.
 * "£1,234.50" and "1234.5" both read as 1234.5.
 */
export function parsePrice(value) {
  const s = String(value ?? '').replace(/[£,\s]/g, '');
  if (!/^\d+(\.\d{1,4})?$/.test(s)) return null;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : null;
}

const sameMoney = (a, b) => Math.abs((Number(a) || 0) - (Number(b) || 0)) < 0.00005;

/**
 * What is wrong with one chosen action, or null when it can be done. The
 * window greys its button with this; the server refuses with it.
 *
 * @param {{ action: string, note?: string, prices?: Object<string, any> }} a
 * @param {Array<{ line_id: number, unit_price: any }>} [lines] the invoice's lines of the product (edit_price)
 */
export function actionProblem(a, lines = []) {
  if (!a || !ACTION_VALUES.has(a.action)) return 'Choose what to do with it';
  if (a.action === 'leave') return null;
  if (!cleanNote(a.note)) return 'Add a note — it is kept with what is done';
  if (a.action !== 'edit_price') return null;
  const list = (lines || []).filter(l => l && l.line_id != null);
  if (!list.length) return 'It has no lines of this product to change';
  const prices = a.prices && typeof a.prices === 'object' ? a.prices : {};
  let changed = 0;
  for (const l of list) {
    const p = parsePrice(prices[l.line_id]);
    if (p === null) return 'Enter a new price for each line — more than £0, at most 4 decimal places';
    if (!sameMoney(p, l.unit_price)) changed += 1;
  }
  if (!changed) return 'The new price is the price it already has';
  return null;
}

/**
 * The price most of a variation's lines carry, when one price is on more
 * lines than any other; null when none is (two lines, two prices).
 * @param {Array<{ unit_price: any }>} lines
 */
export function usualPrice(lines) {
  const counts = new Map();
  for (const l of lines || []) {
    const p = Number(l?.unit_price);
    if (!Number.isFinite(p) || p <= 0) continue;
    const k = p.toFixed(4);
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  if (!ranked.length || (ranked[1] && ranked[1][1] === ranked[0][1])) return null;
  return { price: Number(ranked[0][0]), lines: ranked[0][1], of: [...counts.values()].reduce((s, n) => s + n, 0) };
}

/** How many of these chosen actions do something (a Leave it does only with a note). */
export function countActions(actions) {
  return (actions || []).filter(actionDoesSomething).length;
}

const ukDay = (v) => {
  if (!v) return '';
  const d = String(v).slice(0, 10);
  return ISO_DAY.test(d) ? lockMonthUkDate(d) : '';
};

/**
 * A decision in words: "Approved by Cam Seymour, 03/11/2026" / "Not reviewed
 * yet"; with `{ withNote: true }` its note after it — "… — 'Agreed price for
 * October'".
 */
export function decisionWords(decision, { withNote = false } = {}) {
  if (!decision) return 'Not reviewed yet';
  const who = decision.decided_by_name ? ` by ${decision.decided_by_name}` : '';
  const when = ukDay(decision.decided_at) ? `, ${ukDay(decision.decided_at)}` : '';
  const note = withNote && cleanNote(decision.note) ? ` — '${cleanNote(decision.note)}'` : '';
  return `${decision.approved ? 'Approved' : 'Not approved'}${who}${when}${note}`;
}

/** The label of an action. */
export function actionLabel(action) {
  return (VARIATION_ACTIONS.find(a => a.value === action) || {}).label || 'Leave it';
}

/**
 * The one question before the actions are done: "Do these 3 actions?" with
 * what each kind comes to. Pure.
 * @param {Array<{ action: string }>} actions
 */
export function performQuestion(actions) {
  const n = countActions(actions);
  const by = (v) => (actions || []).filter(a => a && a.action === v).length;
  const parts = [];
  const prices = by('edit_price');
  const flags = by('flag');
  const drafts = by('draft_flag');
  if (prices) parts.push(`${prices} invoice${prices === 1 ? '' : 's'} at a new sale price (changed in Xero too where it is there)`);
  if (drafts) parts.push(`${drafts} made a draft and flagged`);
  if (flags) parts.push(`${flags} flagged, left as ${flags === 1 ? 'it is' : 'they are'}`);
  const noted = (actions || []).filter(a => a && a.action === 'leave' && cleanNote(a.note)).length;
  if (noted) parts.push(`${noted} left as ${noted === 1 ? 'it is' : 'they are'} with a note`);
  return {
    title: `Do these ${n} action${n === 1 ? '' : 's'}?`,
    message: `${parts.join('; ')}. Each is recorded in the invoice's history with its note. One that cannot be done is said, and the others still go ahead.`
  };
}

/**
 * Who may clear a flag: the person who raised it, or an administrator (a
 * safety valve for someone who has left). Pure.
 * @param {{ flagged_by?: number|null }} flag
 * @param {{ userId?: number|null, isAdmin?: boolean }} who
 * @param {string|null} [flaggedByName]
 * @returns {string|null} why not, or null
 */
export function clearFlagBlock(flag, { userId = null, isAdmin = false } = {}, flaggedByName = null) {
  if (!flag) return 'That flag is not there';
  if (flag.cleared_at) return 'That flag has already been cleared';
  if (isAdmin) return null;
  if (flag.flagged_by != null && userId != null && Number(flag.flagged_by) === Number(userId)) return null;
  const who = flaggedByName || flag.flagged_by_name || 'the person who flagged it';
  return `Only ${who} (who flagged it) or an administrator can clear it`;
}

// ---------------------------------------------------------------------------
// 2.0.0 — one step: a choice per variation, saved as it goes
// ---------------------------------------------------------------------------

/**
 * What can be chosen for a variation, in the order the screen offers them.
 * Approve and Leave it are saved as the decision (approved / not approved,
 * with the optional note); the others are done to its invoices, one by one.
 */
export const VARIATION_CHOICES = Object.freeze([
  { value: 'approve', label: 'Approve', short: 'Approve', icon: 'check_circle', kind: 'decision' },
  { value: 'leave', label: 'Leave it', short: 'Leave it', icon: 'do_not_disturb_on', kind: 'decision' },
  { value: 'edit_price', label: 'Edit the sale price', short: 'Edit price', icon: 'edit', kind: 'invoice' },
  { value: 'flag', label: 'Flag it', short: 'Flag', icon: 'flag', kind: 'invoice' },
  { value: 'draft_flag', label: 'Make it a draft and flag it', short: 'Draft & flag', icon: 'edit_note', kind: 'invoice' }
]);

/** Is this choice done to the invoices (rather than saved as the decision)? */
export function isInvoiceChoice(choice) {
  return choice === 'edit_price' || choice === 'flag' || choice === 'draft_flag';
}

/** A choice's label. */
export function choiceLabel(choice) {
  return (VARIATION_CHOICES.find(c => c.value === choice) || {}).label || 'To do';
}

const textOf = (v) => String(v ?? '').trim();
const cmpText = (a, b) => textOf(a).localeCompare(textOf(b), 'en-GB', { numeric: true, sensitivity: 'base' });
/** Blank last, else text order. */
const cmpBlankLast = (a, b) => {
  const ea = !textOf(a);
  const eb = !textOf(b);
  if (ea !== eb) return ea ? 1 : -1;
  return cmpText(a, b);
};

/**
 * THE row order, everywhere the review lists things: customer by account
 * number (none last) then name; product by item code (none last) then words.
 */
export function compareVariations(a, b) {
  return cmpBlankLast(a.account_no, b.account_no)
    || cmpText(a.account_name, b.account_name)
    || (Number(a.account_id) - Number(b.account_id))
    || cmpBlankLast(a.item_code, b.item_code)
    || cmpText(a.description || a.product_key, b.description || b.product_key)
    || cmpText(a.product_key, b.product_key);
}

/** Invoices: by date, then number. */
export function compareInvoices(a, b) {
  return cmpText(String(a.invoice_date || '').slice(0, 10), String(b.invoice_date || '').slice(0, 10))
    || cmpText(a.invoice_no, b.invoice_no)
    || (Number(a.invoice_id) - Number(b.invoice_id));
}

/** The variations (and each one's invoices) in THE row order. Pure; new arrays. */
export function sortVariations(list) {
  return (list || [])
    .map(v => ({ ...v, invoices: [...(v.invoices || [])].sort(compareInvoices) }))
    .sort(compareVariations);
}

const latestDone = (list) => [...list].sort((a, b) => String(a.at || '').localeCompare(String(b.at || ''))).pop();

/**
 * Where a variation stands. `draftChoice` is a choice made on the screen and
 * not yet acted on (an invoice action chosen, applied to none yet).
 *
 *   to_do     nothing decided yet
 *   changed   decided, but its lines or prices are not what they were then
 *   approved  approved (resolved)
 *   left      not approved, left as it is (resolved)
 *   acting    an invoice action chosen; `todo` invoices still to act on
 *   acted     every invoice acted on — or left as it is (resolved)
 *
 * @returns {{ state, resolved: boolean, choice: string|null, todo: number, done: number, text: string }}
 */
export function resolutionOf(v, draftChoice = null) {
  const d = v && v.decision;
  const invoices = (v && v.invoices) || [];
  const acted = invoices.filter(i => i.done);
  const kinds = acted.filter(i => i.done.action !== 'leave');
  const todo = invoices.length - acted.length;
  const savedChoice = !d ? null : (d.approved ? 'approve' : (kinds.length ? latestDone(kinds.map(i => i.done)).action : 'leave'));
  if (draftChoice && isInvoiceChoice(draftChoice) && draftChoice !== savedChoice) {
    const left = invoices.filter(i => !i.done || i.done.action !== draftChoice).length;
    return { state: 'acting', resolved: false, choice: draftChoice, todo: left, done: invoices.length - left,
      text: `${choiceLabel(draftChoice)} — apply it to each invoice` };
  }
  const base = { choice: savedChoice, todo, done: acted.length };
  if (!d) return { ...base, state: 'to_do', resolved: false, text: 'To do' };
  if (d.changed) return { ...base, state: 'changed', resolved: false, text: 'Its lines or prices changed since — look again' };
  if (d.approved) return { ...base, state: 'approved', resolved: true, text: 'Approved' };
  if (!kinds.length) return { ...base, state: 'left', resolved: true, text: 'Left as it is' };
  const label = choiceLabel(savedChoice);
  if (todo) return { ...base, state: 'acting', resolved: false, text: `${label} — ${acted.length} of ${invoices.length} done, ${todo} to do` };
  const rest = kinds.length === invoices.length
    ? (invoices.length === 1 ? 'its invoice' : `all ${invoices.length} invoices`)
    : `${kinds.length} of ${invoices.length} (the rest left as they are)`;
  return { ...base, state: 'acted', resolved: true, text: `${label} — done on ${rest}` };
}

function fmt4(n) {
  return (Number(n) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
}

/** "Price edited 03/11/2026 by Jade Games: £310.00 → £300.00". */
export function doneWords(done) {
  if (!done) return '';
  const when = ukDay(done.at) ? ` ${ukDay(done.at)}` : '';
  const who = done.by ? ` by ${done.by}` : '';
  const label = { leave: 'Left as it is', edit_price: 'Price edited', flag: 'Flagged', draft_flag: 'Made a draft and flagged' }[done.action]
    || actionLabel(done.action);
  const prices = done.action === 'edit_price' && Array.isArray(done.lines) && done.lines.length
    ? `: ${done.lines.map(l => `£${fmt4(l.unit_price && l.unit_price.old)} → £${fmt4(l.unit_price && l.unit_price.new)}`).join(', ')}`
    : '';
  return `${label}${when}${who}${prices}`;
}

/**
 * The prices before the FIRST price edit this review made to an invoice
 * (`edits` oldest first) — what "Put back" puts back — or null.
 * @returns {Object<string, number>|null} { [line id]: price }
 */
export function originalPrices(edits) {
  const out = {};
  for (const e of edits || []) {
    for (const l of e.lines || []) {
      const id = String(l.invoice_line_id);
      if (!(id in out) && l.unit_price && l.unit_price.old != null) out[id] = Number(l.unit_price.old);
    }
  }
  return Object.keys(out).length ? out : null;
}

/**
 * The customers, in the row order, each with its variations and how many are
 * resolved. `drafts` = { [variation key]: { choice } } from the screen.
 */
export function customersOf(variations, drafts = {}) {
  const out = [];
  const byId = new Map();
  for (const v of sortVariations(variations)) {
    if (!byId.has(v.account_id)) {
      const c = { account_id: v.account_id, account_name: v.account_name, account_no: v.account_no, variations: [] };
      byId.set(v.account_id, c);
      out.push(c);
    }
    byId.get(v.account_id).variations.push(v);
  }
  return out.map(c => {
    const resolved = c.variations.filter(v => resolutionOf(v, ((drafts || {})[v.key] || {}).choice || null).resolved).length;
    return { ...c, resolved, total: c.variations.length, done: resolved === c.variations.length };
  });
}

/** The summary bar's figures. */
export function summaryOf(variations, drafts = {}) {
  const out = { variations: 0, customers: 0, resolved: 0, to_do: 0, changed: 0, approve: 0, leave: 0, edit_price: 0, flag: 0, draft_flag: 0 };
  const customers = new Set();
  for (const v of variations || []) {
    const r = resolutionOf(v, ((drafts || {})[v.key] || {}).choice || null);
    out.variations += 1;
    customers.add(v.account_id);
    if (r.resolved) out.resolved += 1; else out.to_do += 1;
    if (r.state === 'changed') out.changed += 1;
    if (r.resolved && r.choice && out[r.choice] !== undefined) out[r.choice] += 1;
  }
  out.customers = customers.size;
  return out;
}

/**
 * The one quick question before an action that writes to an invoice, with
 * every warning the server gave for it (gone out to the customer, the till,
 * Xero, the order line keeping its price).
 * @param {'edit_price'|'put_back'|'flag'|'draft_flag'} action
 * @param {Array<{ invoice_no: string, warnings?: string[] }>} invoices
 */
export function writeQuestion(action, invoices) {
  const list = invoices || [];
  const nos = list.map(i => i.invoice_no).filter(Boolean);
  const which = nos.length === 1 ? nos[0] : `these ${nos.length} invoices (${nos.join(', ')})`;
  const title = {
    edit_price: `Change the sale price on ${which}?`,
    put_back: `Put the old price back on ${which}?`,
    flag: `Flag ${which}?`,
    draft_flag: `Make ${which} a draft and flag ${nos.length === 1 ? 'it' : 'them'}?`
  }[action] || `Do this to ${which}?`;
  const what = {
    edit_price: 'The invoice is changed (in Xero too, where it is there) and the note is kept as the reason.',
    put_back: 'The price goes back to what it was before this review changed it — in Xero too, where it is there.',
    flag: "It stays in everyone's Alerts with the note until you (or an administrator) clear the flag. The invoice is not changed.",
    draft_flag: 'It comes off statements until the flag is cleared — clearing it approves it again.'
  }[action] || '';
  const warnings = [...new Set(list.flatMap(i => i.warnings || []))];
  return { title, message: [what, ...warnings].filter(Boolean).join('\n\n') };
}
